/**
 * Règles du téléphone en fin de séance — quel écran il montre, quel bouton il
 * porte, et quelle charge part vers `POST /sessions-qcm/{id}/etape/`.
 *
 * C'est la table de transitions du serveur (`_etapes_atteignables`, views.py,
 * phase 2) vue du client : les deux doivent dire la même chose, sans quoi le
 * professeur verrait un bouton que l'endpoint refuse. Extraite de la vue pour
 * être testable sans DOM, comme `revueCorrection.js` l'est côté projection.
 *
 * Deux principes, hérités du chantier :
 *
 * - **L'ÉTAPE FAIT FOI, plus le statut** (D3). La séance reste `en_cours`
 *   pendant le podium puis la revue ; brancher sur `statut === 'terminee'`
 *   n'atteindrait jamais ces deux écrans. Symétriquement, une revue REJOUÉE
 *   (D11) tourne sur une séance `terminee` et doit rester pilotable.
 * - **Le chemin du podium est unique.** Quand `podium_actif`, la fin passe par
 *   `podium` même en correction consécutive : deux déclencheurs de podium
 *   auraient divergé au premier écran ajouté.
 *
 * Les valeurs par défaut ci-dessous sont celles du modèle (`models.py` :
 * `podium_actif=True`, `mode_correction='finale'`, `etape='quiz'`,
 * `revue_ordre=0`) : une session dont le champ manque se comporte comme le
 * serveur la traiterait.
 */

/**
 * L'écran que le téléphone doit rendre.
 *
 * `'attente'` est l'avant-lancement, `'quiz'` le scan plein écran, `'pilotage'`
 * les commandes de fin (podium et revue), `'fin'` l'écran de clôture.
 *
 * Le cas `statut === 'terminee'` avec `etape === 'quiz'` est celui des séances
 * closes AVANT la migration 0067, dont le défaut de colonne a rempli `etape` :
 * elles montraient l'écran de fin, elles continuent.
 */
export const ecranTelephone = ({ statut, etape }) => {
  if (statut === 'en_attente') return 'attente';
  if (etape === 'podium' || etape === 'revue') return 'pilotage';
  if (statut === 'terminee') return 'fin';
  return 'quiz';
};

/**
 * « Montrer au projo » est-il proposé ? (D9)
 *
 * En correction finale, le bouton DISPARAÎT — il n'est pas grisé. Un bouton de
 * secours rendrait le réglage indécidable : deux séances « correction finale »
 * n'auraient pas le même déroulé selon que le professeur a résisté ou non.
 */
export const revealDisponible = ({ mode_correction: modeCorrection } = {}) =>
  modeCorrection === 'consecutive';

/** Le déroulé demande-t-il une revue après le corps du quizz ? */
const revueAttendue = (modeCorrection) => modeCorrection !== 'consecutive';

/**
 * Le geste PRINCIPAL de fin de séance : le libellé du bouton vert et la charge
 * du `POST /etape/`, ou `null` quand il n'y a plus rien à piloter.
 *
 * Chaque branche correspond à une ligne de `_etapes_atteignables`. Depuis
 * `fin`, le serveur n'accepte que `revue` (rejouer, D11) — mais le point
 * d'entrée de la reprise est le tableau de bord (phase 6), pas ce bouton :
 * le téléphone rendu sur une séance close et finie n'a rien à proposer.
 *
 * @param totalQuestions nombre de questions de la séance ; borne haute de la
 *   revue, exactement comme le serveur la lit en base.
 */
export const gesteFin = ({
  etape = 'quiz',
  revue_ordre: revueOrdre = 0,
  podium_actif: podiumActif = true,
  mode_correction: modeCorrection = 'finale',
  totalQuestions = 0,
} = {}) => {
  // Un seul mot sur le bouton, du lancement à la clôture (chantier jeton,
  // 2026-09-04) : « Suivant » pour tout pas intermédiaire, « Terminer » pour le
  // dernier. C'est la projection qui dit ce que le pas a fait.
  const apresLeQuiz = revueAttendue(modeCorrection)
    ? { libelle: 'Suivant', charge: { etape: 'revue', revue_ordre: 1 } }
    : { libelle: 'Terminer', charge: { etape: 'fin' } };

  if (etape === 'quiz') {
    return podiumActif
      ? { libelle: 'Suivant', charge: { etape: 'podium' } }
      : apresLeQuiz;
  }
  if (etape === 'podium') return apresLeQuiz;
  if (etape === 'revue') {
    return revueOrdre < totalQuestions
      ? {
        libelle: 'Suivant',
        charge: { etape: 'revue', revue_ordre: revueOrdre + 1 },
      }
      : { libelle: 'Terminer', charge: { etape: 'fin' } };
  }
  return null;
};

/**
 * Le geste « précédent » de la revue, ou `null` quand il n'y a rien derrière.
 *
 * Il n'existe que DANS la revue : le serveur refuse `podium → podium` comme
 * `revue → podium`, et remonter de la revue au classement referait défiler
 * l'ordre de fin à l'envers.
 */
export const gestePrecedent = ({ etape, revue_ordre: revueOrdre = 0 } = {}) => (
  etape === 'revue' && revueOrdre > 1
    ? { libelle: 'Précédent', charge: { etape: 'revue', revue_ordre: revueOrdre - 1 } }
    : null
);

/**
 * Ce que le professeur lit en haut de l'écran de pilotage : où en est la
 * projection, puisqu'il ne la voit pas depuis son téléphone.
 */
export const titrePilotage = ({ etape, revue_ordre: revueOrdre = 0, totalQuestions = 0 } = {}) => {
  if (etape === 'podium') return 'Podium projeté';
  if (etape === 'revue') return `Correction · question ${revueOrdre}/${totalQuestions}`;
  return '';
};
